import React, { useState, useEffect, useCallback } from 'react';
import { Container, Tabs, Tab, Form, Button, Card, Alert, Row, Col, Table, Spinner } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import RecordCard from '../components/RecordCard';
import { isDemoMode } from '../utils/demo';

const initialSettings = {
  email: '',
  firstName: '',
  lastName: '',
  smtpServer: 'smtp.gmail.com',
  smtpPort: '587',
  smtpUsername: '',
  smtpPassword: '',
  airbnbApiKey: '',
  agodaApiKey: '',
  bookingApiKey: '',
};

const initialUserForm = {
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
};

const savedSettingKeys = [
  'smtpServer',
  'smtpPort',
  'smtpUsername',
  'smtpPassword',
  'airbnbApiKey',
  'agodaApiKey',
  'bookingApiKey',
];

const getSettingType = (key) => {
  if (key === 'smtpPort') return 'number';
  if (key.toLowerCase().includes('password') || key.toLowerCase().includes('key')) return 'secret';
  return 'string';
};

function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageVariant, setMessageVariant] = useState('success');
  const [settings, setSettings] = useState(initialSettings);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(initialUserForm);

  const showMessage = (text, variant = 'success') => {
    setMessage(text);
    setMessageVariant(variant);
    window.setTimeout(() => setMessage(''), 3500);
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, settingsRes, usersRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.ME),
        apiClient.get(API_ENDPOINTS.SETTINGS),
        apiClient.get(API_ENDPOINTS.USERS),
      ]);

      const savedSettings = settingsRes.data.reduce((acc, item) => {
        acc[item.setting_key] = item.setting_value ?? '';
        return acc;
      }, {});

      setSettings({
        ...initialSettings,
        ...savedSettings,
        email: profileRes.data.email || '',
        firstName: profileRes.data.first_name || '',
        lastName: profileRes.data.last_name || '',
      });
      setUsers(usersRes.data);
    } catch (err) {
      showMessage(err.response?.data?.detail || '設定の取得に失敗しました', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put(API_ENDPOINTS.ME, {
        first_name: settings.firstName || null,
        last_name: settings.lastName || null,
        email: settings.email,
      });

      await Promise.all(savedSettingKeys.map((key) => (
        apiClient.post(API_ENDPOINTS.SETTINGS, {
          setting_key: key,
          setting_value: String(settings[key] ?? ''),
          setting_type: getSettingType(key),
        })
      )));

      showMessage('設定を保存しました');
      await fetchSettings();
    } catch (err) {
      showMessage(err.response?.data?.detail || '設定の保存に失敗しました', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    setSaving(true);
    try {
      await apiClient.post(API_ENDPOINTS.USERS, userForm);
      setUserForm(initialUserForm);
      showMessage('ユーザーを追加しました');
      await fetchSettings();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'ユーザーの追加に失敗しました', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setSaving(true);
    try {
      await apiClient.delete(`${API_ENDPOINTS.USERS}/${userId}`);
      showMessage('ユーザーを削除しました');
      await fetchSettings();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'ユーザーの削除に失敗しました', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // デモ環境ではユーザーの追加・削除をサーバー側で拒否する(本番では管理者が操作できる)
  const demoMode = isDemoMode();

  const canCreateUser = (
    userForm.username &&
    userForm.email &&
    userForm.password &&
    !demoMode
  );

  if (loading) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  // ユーザー行アクション(テーブル/カードで共用)
  const renderUserActions = (user) => (
    <Button
      variant="outline-danger"
      size="sm"
      onClick={() => handleDeleteUser(user.id)}
      disabled={saving || users.length <= 1 || demoMode}
    >
      削除
    </Button>
  );

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">設定</h1>
      </div>

      {message && <Alert variant={messageVariant}>{message}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'general')} className="mb-3">
        <Tab eventKey="general" title="一般設定">
          <Card>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>名</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={settings.firstName}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>姓</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={settings.lastName}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>メールアドレス</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={settings.email}
                    onChange={handleInputChange}
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  disabled={saving || !settings.email}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="mail" title="メール設定">
          <Card>
            <Card.Body>
              <Form>
                <h5 className="mb-3">SMTP設定</h5>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>SMTPサーバー</Form.Label>
                      <Form.Control
                        type="text"
                        name="smtpServer"
                        value={settings.smtpServer}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>SMTPポート</Form.Label>
                      <Form.Control
                        type="number"
                        name="smtpPort"
                        value={settings.smtpPort}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SMTPユーザー名</Form.Label>
                      <Form.Control
                        type="text"
                        name="smtpUsername"
                        value={settings.smtpUsername}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SMTPパスワード</Form.Label>
                      <Form.Control
                        type="password"
                        name="smtpPassword"
                        value={settings.smtpPassword}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="integrations" title="外部API連携">
          <Card>
            <Card.Body>
              <Form>
                <h5 className="mb-3">Airbnb</h5>
                <Form.Group className="mb-3">
                  <Form.Label>APIキー</Form.Label>
                  <Form.Control
                    type="password"
                    name="airbnbApiKey"
                    value={settings.airbnbApiKey}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <hr />

                <h5 className="mb-3">Agoda</h5>
                <Form.Group className="mb-3">
                  <Form.Label>APIキー</Form.Label>
                  <Form.Control
                    type="password"
                    name="agodaApiKey"
                    value={settings.agodaApiKey}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <hr />

                <h5 className="mb-3">Booking.com</h5>
                <Form.Group className="mb-3">
                  <Form.Label>APIキー</Form.Label>
                  <Form.Control
                    type="password"
                    name="bookingApiKey"
                    value={settings.bookingApiKey}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="users" title="ユーザー管理">
          {demoMode && (
            <Alert variant="warning">
              デモ環境ではユーザーの追加・削除はできません。本番環境ではログイン後にこの画面から操作できます。
            </Alert>
          )}
          <Card className="mb-3">
            <Card.Body>
              <h5 className="mb-3">ユーザー追加</h5>
              <Form>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>ユーザー名</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={userForm.username}
                        onChange={handleUserInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>メールアドレス</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={userForm.email}
                        onChange={handleUserInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>初期パスワード</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={userForm.password}
                        onChange={handleUserInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>名</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={userForm.first_name}
                        onChange={handleUserInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>姓</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={userForm.last_name}
                        onChange={handleUserInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  variant="primary"
                  onClick={handleCreateUser}
                  disabled={saving || !canCreateUser}
                >
                  {saving ? '追加中...' : 'ユーザー追加'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <h5 className="mb-3">ユーザー一覧</h5>
              <div className="d-none d-lg-block">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ユーザー名</th>
                    <th>氏名</th>
                    <th>メール</th>
                    <th>状態</th>
                    <th>登録日</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">ユーザーはまだ登録されていません</td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{[user.last_name, user.first_name].filter(Boolean).join(' ') || '-'}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                            {user.is_active ? '有効' : '無効'}
                          </span>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString('ja-JP')}</td>
                        <td>{renderUserActions(user)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              </div>

              {/* モバイル: カード型リスト */}
              <div className="d-lg-none">
                {users.length === 0 ? (
                  <p className="text-center text-muted">ユーザーはまだ登録されていません</p>
                ) : (
                  users.map((user) => (
                    <RecordCard
                      key={user.id}
                      title={user.username}
                      subtitle={[user.last_name, user.first_name].filter(Boolean).join(' ') || ''}
                      badges={[
                        <span key="s" className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {user.is_active ? '有効' : '無効'}
                        </span>,
                      ]}
                      fields={[
                        { label: 'メール', value: user.email },
                        { label: '登録日', value: new Date(user.created_at).toLocaleDateString('ja-JP') },
                      ]}
                      actions={renderUserActions(user)}
                    />
                  ))
                )}
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
}

export default Settings;
